/**
 * Canonical enums for Hotline, expressed as zod schemas with inferred TS types.
 *
 * SQLite has no native enums, so the Prisma schema stores these as `String`/`Int`
 * columns; THESE schemas are the single source of truth and are validated at the
 * application layer. Keep the string values in lock-step with the comments in
 * `apps/backend/prisma/schema.prisma`.
 */
import type { z } from 'zod';
import { enum as zEnum, literal, union } from 'zod';

// ─────────────────────────────────────────────────────────────────────────────
// CaptureLevel (0–3) — how much the widget collects. Server is authoritative;
// payloads above the per-instance level are rejected. Modeled as a numeric union
// so the literal values 0|1|2|3 survive round-trips (SQLite Int column).
// ─────────────────────────────────────────────────────────────────────────────

export const CaptureLevelSchema = union([literal(0), literal(1), literal(2), literal(3)]);
export type CaptureLevel = z.infer<typeof CaptureLevelSchema>;

/** Named constants for the four capture levels (L0 text-only … L3 +replay). */
export const CaptureLevel = {
  TextOnly: 0,
  Diagnostics: 1,
  Context: 2,
  Replay: 3,
} as const satisfies Record<string, CaptureLevel>;

/** All valid capture levels, ascending. */
export const CAPTURE_LEVELS: readonly CaptureLevel[] = [0, 1, 2, 3] as const;

// ─────────────────────────────────────────────────────────────────────────────
// TicketStatus — lifecycle of a FeedbackSession ("ticket").
// ─────────────────────────────────────────────────────────────────────────────

export const TicketStatusSchema = zEnum([
  'open',
  'triaged',
  'in_progress',
  'resolved',
  'closed',
  'spam',
]);
export type TicketStatus = z.infer<typeof TicketStatusSchema>;
export const TICKET_STATUSES = TicketStatusSchema.options;

// ─────────────────────────────────────────────────────────────────────────────
// Role — one per level of the entity hierarchy:
//
//   platform (uber-admin)  →  organization  →  project (widget instance)  →  feedback item
//   META_ADMIN                ORG_ADMIN        PROJECT_ADMIN                 REPORTER
//
// A user is attached to exactly ONE entity and administers everything beneath
// it; there are no granular per-capability grants. The single exception is
// REPORTER, which is attached to MANY individual feedback items (the FeedbackAccess
// join) rather than to one subtree — a customer who reported three bugs can see
// those three and nothing else.
//
// See `access.ts` for the rules that turn a role into a query scope.
// ─────────────────────────────────────────────────────────────────────────────

export const RoleSchema = zEnum(['META_ADMIN', 'ORG_ADMIN', 'PROJECT_ADMIN', 'REPORTER']);
export type Role = z.infer<typeof RoleSchema>;
export const ROLES = RoleSchema.options;

// ─────────────────────────────────────────────────────────────────────────────
// Channel — how the feedback arrived.
// ─────────────────────────────────────────────────────────────────────────────

export const ChannelSchema = zEnum(['text', 'voice', 'implicit']);
export type Channel = z.infer<typeof ChannelSchema>;
export const CHANNELS = ChannelSchema.options;

// ─────────────────────────────────────────────────────────────────────────────
// AttachmentKind — kind of captured blob. `file` is a user-attached file
// (image/pdf) added in the widget; the others are auto-captured.
// ─────────────────────────────────────────────────────────────────────────────

export const AttachmentKindSchema = zEnum(['screenshot', 'audio', 'replay', 'file']);
export type AttachmentKind = z.infer<typeof AttachmentKindSchema>;
export const ATTACHMENT_KINDS = AttachmentKindSchema.options;

// ─────────────────────────────────────────────────────────────────────────────
// Locale — UI languages the widget renders (and the closed-loop email mirrors).
// Keep in lock-step with the widget i18n tables (apps/widget/src/i18n) and
// apps/backend/src/email/email.templates.ts.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Languages the widget ships UI copy for: the 16 most widely spoken in Europe,
 * ordered by number of speakers there. A visitor's browser language selects one
 * automatically; an operator can pin a specific one per widget instance
 * (`branding.defaultLanguage`) or per embed (`data-lang`). See docs/i18n.md for
 * how to add another.
 *
 * The backend's reply-email templates keep their OWN, smaller list
 * (email.templates.ts): shipping widget copy is cheap, but an outbound email in a
 * language nobody has proofread is not, so the two lists are deliberately
 * decoupled and an unsupported email locale falls back to English.
 */
export const LocaleSchema = zEnum([
  'en', // English
  'ru', // Русский
  'de', // Deutsch
  'fr', // Français
  'it', // Italiano
  'es', // Español
  'pl', // Polski
  'uk', // Українська
  'ro', // Română
  'nl', // Nederlands
  'tr', // Türkçe
  'pt', // Português
  'el', // Ελληνικά
  'cs', // Čeština
  'sv', // Svenska
  'hu', // Magyar
]);
export type Locale = z.infer<typeof LocaleSchema>;
export const LOCALES = LocaleSchema.options;

// ─────────────────────────────────────────────────────────────────────────────
// Secondary enums referenced across DTOs (kept here so every slice agrees).
// ─────────────────────────────────────────────────────────────────────────────

/** Provider selectable per widget-instance for the live/voice agent. */
export const AgentProviderSchema = zEnum(['fake', 'openai', 'gemini']);
export type AgentProvider = z.infer<typeof AgentProviderSchema>;

/** Organization plan tier (billing modeled only). */
export const OrgPlanSchema = zEnum(['free', 'pro', 'enterprise']);
export type OrgPlan = z.infer<typeof OrgPlanSchema>;

/** Organization lifecycle status. */
export const OrgStatusSchema = zEnum(['active', 'suspended']);
export type OrgStatus = z.infer<typeof OrgStatusSchema>;

/** WidgetInstance lifecycle status. */
export const WidgetInstanceStatusSchema = zEnum(['active', 'disabled']);
export type WidgetInstanceStatus = z.infer<typeof WidgetInstanceStatusSchema>;

/** Console log levels captured by the widget. */
export const ConsoleLevelSchema = zEnum(['log', 'info', 'warn', 'error', 'debug']);
export type ConsoleLevel = z.infer<typeof ConsoleLevelSchema>;

/** Roles used inside a conversation transcript turn. */
export const TranscriptRoleSchema = zEnum(['user', 'agent', 'system']);
export type TranscriptRole = z.infer<typeof TranscriptRoleSchema>;

/** AI-derived sentiment label. */
export const SentimentSchema = zEnum(['positive', 'neutral', 'negative']);
export type Sentiment = z.infer<typeof SentimentSchema>;

/** Direction of a closed-loop email. */
export const EmailDirectionSchema = zEnum(['inbound', 'outbound']);
export type EmailDirection = z.infer<typeof EmailDirectionSchema>;

/** Delivery status of a closed-loop email. */
export const EmailStatusSchema = zEnum(['queued', 'sent', 'failed']);
export type EmailStatus = z.infer<typeof EmailStatusSchema>;
